import type { Lang, SerieKey } from './types';

/**
 * Etiquetas legibles (es/en) de las claves que publica la API. Cubre TODOS los rubros, categorías,
 * presentaciones y unidades del ejemplo real (contract-fixtures/ejemplo-ultimos.json). Para claves
 * desconocidas se usa `humanizar()`. Las regiones NO se traducen (son topónimos).
 */
type Par = readonly [es: string, en: string];
const pick = (par: Par, lang: Lang): string => (lang === 'en' ? par[1] : par[0]);

const RUBROS: Record<string, Par> = {
  hortalizas: ['Hortalizas', 'Vegetables'],
  bovino: ['Bovino', 'Cattle'],
  frutas: ['Frutas', 'Fruit'],
  bufalino: ['Bufalino', 'Buffalo'],
  lacteos: ['Lácteos', 'Dairy'],
  porcino: ['Porcino', 'Swine'],
};

const CATEGORIAS: Record<string, Par> = {
  // bovino
  toro: ['Toro', 'Bull'],
  vaca: ['Vaca', 'Cow'],
  maute: ['Maute', 'Young bull (maute)'],
  novillo: ['Novillo', 'Steer (novillo)'],
  // bufalino
  baute: ['Baute', 'Buffalo calf (baute)'],
  bufala: ['Búfala', 'Buffalo cow'],
  bufalo: ['Búfalo', 'Buffalo bull'],
  bumaute: ['Bumaute', 'Young buffalo (bumaute)'],
  // porcino
  cerda: ['Cerda', 'Sow'],
  cerdo: ['Cerdo', 'Pig'],
  cerdo_industrial: ['Cerdo industrial', 'Industrial pig'],
  lechon: ['Lechón', 'Piglet'],
  // lácteos
  leche_bufala: ['Leche de búfala', 'Buffalo milk'],
  leche_vaca: ['Leche de vaca', 'Cow milk'],
  queso: ['Queso', 'Cheese'],
  // frutas
  aguacate: ['Aguacate', 'Avocado'],
  durazno_1era: ['Durazno 1.ª', 'Peach, grade 1'],
  fresa: ['Fresa', 'Strawberry'],
  higos: ['Higos', 'Figs'],
  mora: ['Mora', 'Blackberry (mora)'],
  papaya: ['Papaya', 'Papaya'],
  platano: ['Plátano', 'Plantain'],
  // hortalizas
  aji_dulce: ['Ají dulce', 'Sweet pepper (ají dulce)'],
  ajo: ['Ajo', 'Garlic'],
  apio: ['Apio', 'Celery'],
  batata: ['Batata', 'Sweet potato'],
  berenjena: ['Berenjena', 'Eggplant'],
  brocoli: ['Brócoli', 'Broccoli'],
  calabacin: ['Calabacín', 'Zucchini'],
  cebolla: ['Cebolla', 'Onion'],
  cebollin: ['Cebollín', 'Chives'],
  chayota: ['Chayota', 'Chayote'],
  cilantro: ['Cilantro', 'Cilantro'],
  coliflor: ['Coliflor', 'Cauliflower'],
  lechuga: ['Lechuga', 'Lettuce'],
  papa: ['Papa', 'Potato'],
  pepino: ['Pepino', 'Cucumber'],
  pimenton_invernadero: ['Pimentón (invernadero)', 'Bell pepper (greenhouse)'],
  repollo: ['Repollo', 'Cabbage'],
  tomate_manzano: ['Tomate manzano', 'Apple tomato (manzano)'],
  tomate_perita: ['Tomate perita', 'Plum tomato (perita)'],
  yuca: ['Yuca', 'Cassava'],
  zanahoria: ['Zanahoria', 'Carrot'],
};

const PRESENTACIONES: Record<string, Par> = {
  en_pie: ['En pie', 'Live weight'],
  en_canal: ['En canal', 'Carcass'],
  productor: ['Al productor', 'Farm-gate'],
  consumidor: ['Al consumidor', 'Retail'],
  fria: ['Fría', 'Cold'],
  caliente: ['Caliente', 'Fresh'],
  queseras: ['Para queseras', 'For cheesemakers'],
};

/** [singular, plural] por unidad; las abreviaturas (kg, L, t) no cambian. */
const UNIDADES: Record<string, { es: readonly [string, string]; en: readonly [string, string] }> = {
  kg: { es: ['kg', 'kg'], en: ['kg', 'kg'] },
  L: { es: ['L', 'L'], en: ['L', 'L'] },
  t: { es: ['t', 't'], en: ['t', 't'] },
  unidad: { es: ['unidad', 'unidades'], en: ['unit', 'units'] },
  carton30: { es: ['cartón de 30', 'cartones de 30'], en: ['30-egg carton', '30-egg cartons'] },
};

const FUENTES: Record<string, string> = {
  precioganado: 'Precio Ganado',
  asodegaa: 'ASODEGAA',
  ugapaez: 'UGAPAEZ',
  sorgo: 'SORGO',
  'fedeagro-ipaf': 'Fedeagro (IPAF)',
};

const ACENTOS: Record<string, string> = {
  aji: 'ají',
  pimenton: 'pimentón',
  brocoli: 'brócoli',
  calabacin: 'calabacín',
  cebollin: 'cebollín',
  platano: 'plátano',
  lechon: 'lechón',
  lacteos: 'lácteos',
  bufala: 'búfala',
  bufalo: 'búfalo',
  limon: 'limón',
  melon: 'melón',
  sandia: 'sandía',
  maiz: 'maíz',
  mani: 'maní',
  cafe: 'café',
  azucar: 'azúcar',
  pina: 'piña',
};
/** Palabras que, a partir de la segunda, se escriben entre paréntesis: "pimenton_invernadero". */
const CALIFICADORES = new Set([
  'invernadero',
  'lavada',
  'lavado',
  'seleccionada',
  'seleccionado',
  'importada',
  'importado',
  'organica',
  'organico',
]);

const ORDINAL = /^(\d+)(era|ra|da|ta|va|ma|na)$/;
const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Etiqueta de reserva para una clave desconocida: 'aji_dulce' → 'Ají dulce', 'durazno_1era' → 'Durazno 1.ª'. */
export function humanizar(clave: string): string {
  const tokens = clave
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean);
  if (tokens.length === 0) return '';
  const idx = tokens.findIndex((t, i) => i > 0 && CALIFICADORES.has(t.toLowerCase()));
  const render = (t: string): string => {
    const ord = ORDINAL.exec(t.toLowerCase());
    return ord ? `${ord[1]}.ª` : (ACENTOS[t.toLowerCase()] ?? t.toLowerCase());
  };
  const principal = (idx === -1 ? tokens : tokens.slice(0, idx)).map(render).join(' ');
  const calificador = idx === -1 ? '' : ` (${tokens.slice(idx).map(render).join(' ')})`;
  return cap(principal) + calificador;
}

const lookup = (dict: Record<string, Par>, clave: string, lang: Lang): string => {
  const par = dict[clave] ?? dict[clave.toLowerCase()];
  return par ? pick(par, lang) : humanizar(clave);
};

export const rubroLabel = (clave: string, lang: Lang = 'es'): string => lookup(RUBROS, clave, lang);
export const categoriaLabel = (clave: string, lang: Lang = 'es'): string =>
  lookup(CATEGORIAS, clave, lang);

export function presentacionLabel(clave: string | null | undefined, lang: Lang = 'es'): string {
  if (!clave) return lang === 'en' ? 'Unspecified' : 'Sin especificar';
  return lookup(PRESENTACIONES, clave, lang);
}

/** Unidad corta para "$2,55/kg". Con `cantidad` ≠ 1 devuelve el plural ("unidades"). */
export function unidadLabel(
  clave: string | null | undefined,
  lang: Lang = 'es',
  cantidad = 1,
): string {
  if (!clave) return '';
  const entry = UNIDADES[clave] ?? UNIDADES[clave.toLowerCase()];
  if (!entry) return clave;
  return entry[lang][cantidad === 1 ? 0 : 1];
}

/** "El Vigía, Mérida" → "El Vigía" · "Municipio Torres (Carora), Lara" → "Torres (Carora)" · null → "Sin región". */
export function regionCorta(region: string | null | undefined, lang: Lang = 'es'): string {
  const texto = region?.trim();
  if (!texto) return lang === 'en' ? 'No region' : 'Sin región';
  const primera = (texto.split(',')[0] ?? texto).replace(
    /\s*\((?:promedio|referencial)\)\s*$/i,
    '',
  );
  return primera.replace(/^municipio\s+/i, '').trim() || texto;
}

export function fuenteLabel(clave: string | null | undefined, lang: Lang = 'es'): string {
  if (!clave) return lang === 'en' ? 'Unknown source' : 'Fuente desconocida';
  return FUENTES[clave] ?? FUENTES[clave.toLowerCase()] ?? humanizar(clave.replace(/-/g, ' '));
}

/** "Toro en pie", "Papa (al productor)", "Leche de vaca (fría)", "Papaya". */
export function serieLabel(serie: SerieKey, lang: Lang = 'es'): string {
  const categoria = categoriaLabel(serie.categoria, lang);
  if (!serie.presentacion) return categoria;
  const presentacion = presentacionLabel(serie.presentacion, lang).toLowerCase();
  const enLinea =
    lang === 'es' && (serie.presentacion === 'en_pie' || serie.presentacion === 'en_canal');
  return enLinea ? `${categoria} ${presentacion}` : `${categoria} (${presentacion})`;
}
